import { useState } from 'react'

import { Wizard, WizardStep } from '@factorialco/deprecated-design-system'
import { PayrollPolicyFragment, PayrollPolicyPeriodFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'
import { isCurrency } from 'modules/core/lib/money'

import MapToConceptsStep from './_steps/mapToConceptsStep'
import ReviewAndImportStep from './_steps/reviewAndImportStep'
import SelectDataStep from './_steps/selectDataStep'
import { PayrollPayrollSummaryImport, WizardData } from './types'

const DEFAULT_CURRENCY = 'EUR'

interface Props {
  payrollPolicy: PayrollPolicyFragment
  payrollPolicyPeriod: PayrollPolicyPeriodFragment
  payrollSummaryImport: PayrollPayrollSummaryImport
  source: string
  onUpdateImportSummary?: (payrollSummaryImport: PayrollPayrollSummaryImport) => void
  onFinish?: () => void
  onClose?: () => void
}

const ImportSummaryWizard = ({
  payrollSummaryImport,
  payrollPolicy,
  payrollPolicyPeriod,
  source,
  onUpdateImportSummary,
  onFinish,
  onClose
}: Props) => {
  const { employee, company, access } = useCurrentSession()

  const _currency =
    payrollPolicy?.currency ||
    employee?.legalEntity?.currency ||
    company?.currency ||
    DEFAULT_CURRENCY
  const currency = isCurrency(_currency) ? _currency : DEFAULT_CURRENCY

  // Wizard variables
  const initialData: WizardData = {
    payrollPolicy,
    payrollSummaryImport,
    payrollPolicyPeriod,
    source,
    employee,
    company,
    access,
    currency,
    onUpdateImportSummary: (summaryImport: PayrollPayrollSummaryImport) => {
      onUpdateImportSummary?.(summaryImport)
    }
  }
  const [wizardData, setWizardData] = useState<WizardData>(initialData)

  useUnsafeEffect(() => {
    setWizardData(initialData)
  }, [])

  const selectDataCompleted = (data: WizardData) => {
    return (
      (data.worksheetMatches?.name !== undefined &&
        data.worksheetMatches?.last_name !== undefined &&
        data.worksheetMatches?.net_salary !== undefined) ||
      false
    )
  }

  const secondaryAction = {
    action: () => {
      onCloseWizard()
    },
    label: i18n.t('common.cancel')
  }

  const selectDataStep: WizardStep<WizardData> = {
    title: i18n.t('payroll_bundle.payroll_summary_import.wizard.select_data_step.title'),
    hideSteps: true,
    isCompleted: (data) => selectDataCompleted(data),
    secondaryAction: secondaryAction,
    nextText: i18n.t('common.next'),
    render: (props) => <SelectDataStep {...props}></SelectDataStep>
  }

  const mapToConceptsCompleted = (data: WizardData) => {
    if (data.loadingContent) return false

    const { firstNameCell, lastNameCell, netSalaryCell } = data.payrollSummaryImport
    if (!firstNameCell || !lastNameCell || !netSalaryCell) return false

    const labelToConceptId = data.labelToConceptId ?? {}
    const hasAtLeastOneMapping = Object.values(labelToConceptId).some((id) => id != null)
    return hasAtLeastOneMapping
  }

  const mapToConceptsStep: WizardStep<WizardData> = {
    title: i18n.t('payroll_bundle.payroll_summary_import.wizard.map_to_concepts_step.title'),
    hideSteps: true,
    isCompleted: (data) => mapToConceptsCompleted(data ?? wizardData),
    secondaryAction: secondaryAction,
    nextText: i18n.t('common.next'),
    render: (props) => <MapToConceptsStep {...props}></MapToConceptsStep>
  }

  const reviewAndImportStep: WizardStep<WizardData> = {
    title: i18n.t('payroll_bundle.payroll_summary_import.wizard.review_and_import_step.title'),
    hideSteps: true,
    isCompleted: (data: WizardData) => {
      return (data?.employeesSelected?.size || 0) > 0
    },
    secondaryAction: secondaryAction,
    nextText: i18n.tp(
      'payroll_bundle.payroll_summary_import.wizard.review_and_import_step.import_button',
      { count: wizardData?.employeesSelected?.size || 0 }
    ),
    render: (props) => <ReviewAndImportStep {...props}></ReviewAndImportStep>
  }

  const steps = [selectDataStep, mapToConceptsStep, reviewAndImportStep]

  const handleOnDataChanged = (data: WizardData) => {
    setWizardData(data)
  }

  const onOpenAutoFocus = () => {}

  const onFinishWizard = async () => {
    onFinish?.()
  }

  const onCloseWizard = () => {
    onClose?.()
  }

  const descriptionText = i18n.t('payroll_bundle.payroll_summary_import.wizard.description', {
    policy: payrollPolicy.name,
    period: payrollPolicyPeriod.name
  })

  return (
    <Wizard.Modal
      locked
      title={i18n.t('payroll_bundle.payroll_summary_import.wizard.title')}
      description={descriptionText}
      initialData={initialData}
      icon="upload"
      onDataChanged={handleOnDataChanged}
      onSubmit={onFinishWizard}
      onClose={onCloseWizard}
      steps={steps}
      size="large"
      onOpenAutoFocus={onOpenAutoFocus}
    />
  )
}

export default ImportSummaryWizard
